import {
  AusenciaDTO,
  CreateAusenciaDTO,
  CreatePessoaDTO,
  MeuJobDTO,
  PessoaAtribuivelDTO,
  PessoaDTO,
  UpdatePessoaDTO,
} from "@/types/pessoa";
import {
  NotificacaoCanal,
  PessoaFiltrosCriteria,
  PessoaFiltrosOptions,
  PessoaResumoDTO,
  RoleNome,
} from "@workflow/types";
import { PageResponse } from "@/types/http.type";
import {
  PessoaAcessoFiltrosCriteria,
  PessoaAcessoFiltrosOptions,
  PessoaAcessoResumoDTO,
  PessoaAcessoRowDTO,
  PessoaCargoResumoDTO,
  PessoaFeriasDTO,
  PessoaOnlineDTO,
} from "@/types/pessoa-acesso.type";
import { buildQueryParams } from "@/utils/build-query-params";
import { httpClient } from "./httpClient";

interface IPessoaService {
  getPessoas(): Promise<PessoaDTO[]>;
  buscarPessoasAtribuiveis(search?: string, limit?: number): Promise<PessoaAtribuivelDTO[]>;
  getPessoaResumoById(id: number): Promise<PessoaAtribuivelDTO | null>;
  listPessoasPaginado(filter?: PessoaFiltrosCriteria): Promise<PageResponse<PessoaDTO>>;
  getPessoaFiltros(): Promise<PessoaFiltrosOptions>;
  getPessoaResumo(): Promise<PessoaResumoDTO>;
  getResumoAcessos(dias: number): Promise<PessoaAcessoResumoDTO>;
  listAcessos(criteria: PessoaAcessoFiltrosCriteria): Promise<PageResponse<PessoaAcessoRowDTO>>;
  getAcessosFiltros(): Promise<PessoaAcessoFiltrosOptions>;
  getResumoCargos(): Promise<PessoaCargoResumoDTO[]>;
  getPessoasEmFerias(): Promise<PessoaFeriasDTO[]>;
  getPessoasOnline(): Promise<PessoaOnlineDTO[]>;
  getAllPessoas(): Promise<PessoaDTO[]>;
  getPessoasByRole(role: RoleNome): Promise<PessoaDTO[]>;
  getPessoasComRolesEmComum(): Promise<PessoaDTO[]>;
  getPessoaById(id: number): Promise<PessoaDTO>;
  getMe(): Promise<PessoaDTO>;
  getMeusJobs(): Promise<MeuJobDTO[]>;
  uploadFoto(file: File): Promise<PessoaDTO>;
  createPessoa(data: CreatePessoaDTO): Promise<PessoaDTO>;
  updatePessoa(id: number, data: UpdatePessoaDTO): Promise<PessoaDTO>;
  deletePessoa(id: number): Promise<void>;
  ativarPessoa(id: number): Promise<void>;
  createNotificacaoPreferencias(canais: NotificacaoCanal[]): Promise<NotificacaoCanal[]>;
  getUserAusencia(): Promise<AusenciaDTO | null>;
  adicionarAusencia(ausencia: CreateAusenciaDTO): Promise<AusenciaDTO>;
  removerAusencia(): Promise<void>;
  getPessoaAusencia(id: number): Promise<AusenciaDTO | null>;
  adicionarAusenciaPara(id: number, ausencia: CreateAusenciaDTO): Promise<AusenciaDTO>;
  removerAusenciaDe(id: number): Promise<void>;
}

class PessoaService implements IPessoaService {
  private readonly basePath = "/api/v1/pessoas";

  // =========================================================================
  // LEITURA
  // =========================================================================

  public async getMe(): Promise<PessoaDTO> {
    return await httpClient.get<PessoaDTO>(`${this.basePath}/me`);
  }

  public async getMeusJobs(): Promise<MeuJobDTO[]> {
    return await httpClient.get<MeuJobDTO[]>(`${this.basePath}/me/jobs`);
  }

  public async uploadFoto(file: File): Promise<PessoaDTO> {
    const formData = new FormData();
    formData.append("file", file);
    return await httpClient.post<PessoaDTO>(`${this.basePath}/me/foto`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  public async getPessoas(): Promise<PessoaDTO[]> {
    return await httpClient.get<PessoaDTO[]>(this.basePath);
  }

  /** Busca enxuta de pessoas ativas para o seletor de atribuição (server-side). */
  public async buscarPessoasAtribuiveis(
    search?: string,
    limit?: number,
  ): Promise<PessoaAtribuivelDTO[]> {
    const query = buildQueryParams({ search, limit });
    const url = query ? `${this.basePath}/atribuiveis?${query}` : `${this.basePath}/atribuiveis`;
    return await httpClient.get<PessoaAtribuivelDTO[]>(url);
  }

  /** Projeção enxuta de uma pessoa por id — para exibir a pessoa sem o grafo completo. */
  public async getPessoaResumoById(id: number): Promise<PessoaAtribuivelDTO | null> {
    return await httpClient.get<PessoaAtribuivelDTO | null>(`${this.basePath}/${id}/resumo`);
  }

  /** Acessos por dia (pessoas distintas) e adoção geral (ativos vs. já acessaram). */
  public async getResumoAcessos(dias: number): Promise<PessoaAcessoResumoDTO> {
    return await httpClient.get<PessoaAcessoResumoDTO>(
      `${this.basePath}/acessos/resumo?dias=${dias}`,
    );
  }

  /** Listagem paginada/filtrada dos acessos (busca, pessoa, período). */
  public async listAcessos(
    criteria: PessoaAcessoFiltrosCriteria,
  ): Promise<PageResponse<PessoaAcessoRowDTO>> {
    const query = buildQueryParams(criteria);
    const url = query
      ? `${this.basePath}/acessos/listagem?${query}`
      : `${this.basePath}/acessos/listagem`;
    return await httpClient.get<PageResponse<PessoaAcessoRowDTO>>(url);
  }

  /** Opções de filtro da listagem de acessos. */
  public async getAcessosFiltros(): Promise<PessoaAcessoFiltrosOptions> {
    return await httpClient.get<PessoaAcessoFiltrosOptions>(`${this.basePath}/acessos/filtros`);
  }

  /** Quantidade de pessoas ativas por cargo. */
  public async getResumoCargos(): Promise<PessoaCargoResumoDTO[]> {
    return await httpClient.get<PessoaCargoResumoDTO[]>(`${this.basePath}/cargos/resumo`);
  }

  /** Pessoas ativas atualmente em férias/ausência. */
  public async getPessoasEmFerias(): Promise<PessoaFeriasDTO[]> {
    return await httpClient.get<PessoaFeriasDTO[]>(`${this.basePath}/ferias`);
  }

  /** Pessoas ativas com interação recente ("online"). */
  public async getPessoasOnline(): Promise<PessoaOnlineDTO[]> {
    return await httpClient.get<PessoaOnlineDTO[]>(`${this.basePath}/online`);
  }

  public async listPessoasPaginado(
    filter?: PessoaFiltrosCriteria,
  ): Promise<PageResponse<PessoaDTO>> {
    let url = `${this.basePath}/listagem`;

    if (filter) {
      const query = buildQueryParams(filter);
      if (query) url += "?" + query;
    }

    return await httpClient.get<PageResponse<PessoaDTO>>(url);
  }

  public async getPessoaFiltros(): Promise<PessoaFiltrosOptions> {
    return await httpClient.get<PessoaFiltrosOptions>(`${this.basePath}/filtros`);
  }

  public async getPessoaResumo(): Promise<PessoaResumoDTO> {
    return await httpClient.get<PessoaResumoDTO>(`${this.basePath}/resumo`);
  }

  public async getAllPessoas(): Promise<PessoaDTO[]> {
    return await httpClient.get<PessoaDTO[]>(`${this.basePath}/all`);
  }

  public async getPessoasByRole(role: RoleNome): Promise<PessoaDTO[]> {
    return await httpClient.get<PessoaDTO[]>(`${this.basePath}/role/${role}`);
  }

  public async getPessoasComRolesEmComum(): Promise<PessoaDTO[]> {
    return await httpClient.get<PessoaDTO[]>(`${this.basePath}/roles-comuns`);
  }

  public async getPessoaById(id: number): Promise<PessoaDTO> {
    return await httpClient.get<PessoaDTO>(`${this.basePath}/${id}`);
  }

  // =========================================================================
  // AUSÊNCIA
  // =========================================================================

  public async getUserAusencia(): Promise<AusenciaDTO | null> {
    return await httpClient.get<AusenciaDTO | null>(`${this.basePath}/ausencia/me`);
  }

  public async adicionarAusencia(ausencia: CreateAusenciaDTO): Promise<AusenciaDTO> {
    return await httpClient.post<AusenciaDTO>(`${this.basePath}/ausencia`, ausencia);
  }

  public async removerAusencia(): Promise<void> {
    await httpClient.delete<void>(`${this.basePath}/ausencia`);
  }

  // --- Gestão: admin marca/consulta a ausência de outra pessoa ---

  public async getPessoaAusencia(id: number): Promise<AusenciaDTO | null> {
    return await httpClient.get<AusenciaDTO | null>(`${this.basePath}/${id}/ausencia`);
  }

  public async adicionarAusenciaPara(
    id: number,
    ausencia: CreateAusenciaDTO,
  ): Promise<AusenciaDTO> {
    return await httpClient.post<AusenciaDTO>(`${this.basePath}/${id}/ausencia`, ausencia);
  }

  public async removerAusenciaDe(id: number): Promise<void> {
    await httpClient.delete<void>(`${this.basePath}/${id}/ausencia`);
  }

  // =========================================================================
  // ESCRITA / AÇÕES
  // =========================================================================

  public async createPessoa(data: CreatePessoaDTO): Promise<PessoaDTO> {
    return await httpClient.post<PessoaDTO>(this.basePath, data);
  }

  public async updatePessoa(id: number, data: UpdatePessoaDTO): Promise<PessoaDTO> {
    return await httpClient.put<PessoaDTO>(`${this.basePath}/${id}`, data);
  }

  public async ativarPessoa(id: number): Promise<void> {
    // ⚡ Atualizado para PATCH para seguir a semântica do back-end
    await httpClient.patch(`${this.basePath}/${id}/ativar`);
  }

  public async deletePessoa(id: number): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  public async createNotificacaoPreferencias(
    canais: NotificacaoCanal[],
  ): Promise<NotificacaoCanal[]> {
    // O endpoint devolve a preferência inteira ({ id, canais, ... }), não o array —
    // devolver o objeto cru fazia a página quebrar em "canaisInitial is not iterable".
    const preferencia = await httpClient.post<{ canais?: NotificacaoCanal[] }>(
      `${this.basePath}/notificacao-preferencias`,
      canais,
    );
    return preferencia?.canais ?? canais;
  }
}

export const pessoaService = new PessoaService();
